import {Injectable, signal} from '@angular/core';
import {Utils} from '@/classes/utils';
import {Log} from '@/_services/log.service';
import {HttpClient, HttpRequest} from '@angular/common/http';
import {lastValueFrom, throwError, timeout} from 'rxjs';
import {oauth2SyncType} from '@/_services/sync/oauth2pkce';
import {SyncService} from '@/_services/sync/sync.service';
import {LanguageService} from '@/_services/language.service';
import {LangData} from '@/_model/lang-data';
import {EnvironmentService} from '@/_services/environment.service';
import {MessageService} from '@/_services/message.service';
import {WelcomeComponent} from '@/components/welcome/welcome.component';
import {WhatsNewComponent} from '@/components/whats-new/whats-new.component';
import {PeriodShift} from '@/_model/period-shift';
import {DatepickerPeriod} from '@/_model/datepicker-period';
import {AppData, TypeUser, UserType} from '@/_model/app-data';
import {BackendService} from '@/_services/backend.service';
import {MatFormFieldAppearance} from '@angular/material/form-field';
import {ImgurService} from '@/_services/oauth2/imgur.service';

class CustomTimeoutError extends Error {
  constructor() {
    super('It was too slow');
    this.name = 'CustomTimeoutError';
  }
}

export let GLOBALS: GlobalsService;

@Injectable({
  providedIn: 'root'
})
export class GlobalsService {
  version = '1.0';
  appName = 'Tacta';
  crowdinTitle = 'tacta';
  skipStorageClear = false;
  debugFlag = 'debug';
  debugActive = 'yes';
  maxLogEntries = 20;
  storageVersion: string;
  language: LangData;
  theme: string;
  _syncType: oauth2SyncType;
  oauth2AccessToken: string = null;
  currPeriodShift: PeriodShift;
  period: DatepickerPeriod = new DatepickerPeriod();
  dragPos: any = {};
  editColors = false;
  currentUserType: TypeUser;
  ownTheme: any;
  isConfigured = true;
  currentPage: string = 'main';
  pageKeyMain = 'main';
  pageKeySettings = 'settings';
  pageKeyPassword = 'password';
  saveImmediately = true;
  titles: any = {
    settings: $localize`Settings`,
    password: $localize`Passwordchange`,
    dsgvo: $localize`Dataprotection`,
    help: $localize`Information`,
    impressum: $localize`Impressum`,
    welcome: $localize`Welcome to Tacta`,
    whatsnew: $localize`Once upon a time...`,
    linkPicture: $localize`Link Picture`,
    imgurSelector: $localize`Imgur Picture Selector`,
  };
  appearance: MatFormFieldAppearance = 'fill';
  private flags = '';

  constructor(public http: HttpClient,
              public sync: SyncService,
              public ls: LanguageService,
              public env: EnvironmentService,
              public ms: MessageService,
              public imgur: ImgurService,
              public bs: BackendService) {
    GLOBALS = this;
    this.loadWebData();
    const elem = document.querySelector('head>title');
    if (elem != null && this.isLocal) {
      elem.innerHTML = `${elem.innerHTML} (local)`;
    }
    this.loadSharedData().then(_ => {
      this.bs.loginByToken(
        (data) => {
          const ut = GLOBALS.currentUserType?.value;
          this.appData = new AppData();
          this.appData.fillFromBackend(data.data);
          this.appData.permissions = data.perm?.split(',').map((entry: string) => +entry);
          this.appData.usertype = data.type;
          this.currentUserType = GLOBALS.usertypeList.find(e => e.value === ut) ?? GLOBALS.usertypeList[0];
          if (this.storageVersion !== this.version) {
            this.ms.showPopup(WhatsNewComponent, 'whatsnew', {});
          } else {
//            this.currentPage = 'main';
          }
          this.imgur.init();
        }, (_error) => {
          this.ms.showPopup(WelcomeComponent, 'welcome', {});
        });
    });
  }

  _appData = signal<AppData>(null);

  get appData(): AppData {
    return this._appData();
  }

  set appData(value: AppData) {
    this._appData.set(value);
  }

  get usertypeList(): TypeUser[] {
    const ret: TypeUser[] = [];
    for (const key of Object.keys(AppData.UserTypes)) {
      const type = AppData.UserTypes[key];
      if (GLOBALS.appData?.usertype & type.value) {
        ret.push(type);
      }
    }
    return ret;
  }

  get currentUsertypes(): string {
    const ret: string[] = [];
    for (const key of Object.keys(AppData.UserTypes)) {
      const type = AppData.UserTypes[key];
      if (GLOBALS.appData?.usertype & type.value) {
        ret.push(type.label);
      }
    }
    return Utils.join(ret, ', ');
  }

  get usertypes(): any[] {
    const ret: any[] = [];
    for (const key of Object.keys(AppData.UserTypes)) {
      const type = AppData.UserTypes[key];
      if (type.value !== UserType.Admin) {
        ret.push(type);
      }
    }
    return ret;
  }

  _isDebug = signal<boolean>(false);

  get isDebug(): boolean {
    return this._isDebug() && Log.mayDebug;
  }

  set isDebug(value: boolean) {
    if (!Log.mayDebug) {
      value = false;
    }
    if (this._isDebug() !== value) {
      this._isDebug.set(value);
    }
  }

  get mayDebug(): boolean {
    return Log.mayDebug;
  }

  get mayEdit(): boolean {
    return this.may('edit');
  }

  get isAdmin(): boolean {
    return this.may('admin');
  }

  get runsLocal(): boolean {
    return window.location.href.indexOf('/localhost:') >= 0;
  }

  _isLocal = window.location.href.indexOf('/localhost:') >= 0;

  get isLocal(): boolean {
    return this._isLocal;
  }

  set isLocal(value: boolean) {
    this._isLocal = value;
  }

  get appTitle(): string {
    return document.querySelector('head>title').innerHTML;
  }

  loadAppData() {
    this.bs.loadAppData((data) => {
      const ut = GLOBALS.currentUserType?.value;
      this.appData.fillFromJson(data.asJson);
      this.currentUserType = GLOBALS.usertypeList.find(e => e.value === ut) ?? GLOBALS.usertypeList[0];
    });
  }

  async loadSharedData() {
    let storage: any = {};
    try {
      storage = JSON.parse(localStorage.getItem('sharedData')) ?? {};
    } catch {
    }
    let syncData: any = await this.sync.downloadFile(this.env.settingsFilename);
    if (syncData != null) {
      try {
        if (+syncData.s0 > +storage.s0) {
          storage = syncData;
        }
      } catch {
      }
    }

    this.storageVersion = storage.s1;
    // validate values
    this.bs.token = storage.s2;
    this._loadSharedData(storage);
  }

  saveSharedData(): void {
    const storage: any = {
      s0: Date.now(),
      s1: this.version,
      s2: this.bs.token
    };
    const data = JSON.stringify(storage);
    localStorage.setItem('sharedData', data);
    if (this.sync.hasSync) {
      this.sync.uploadFile(this.env.settingsFilename, data);
    }
  }

  loadWebData(): void {
    let storage: any = {};
    try {
      storage = JSON.parse(localStorage.getItem('webData')) ?? {};
    } catch {
    }

    const code = storage.w0 ?? 'en-GB';
    this.language = this.ls.languageList.find((lang) => lang.code === code);
    this._syncType = storage.w1 ?? oauth2SyncType.none;
    this.oauth2AccessToken = storage.w2;
    this.theme = storage.w3 ?? 'standard';
    GLOBALS.ownTheme = storage.w4;

    // validate values
    if (this.oauth2AccessToken == null) {
      this._syncType = oauth2SyncType.none;
    }
  }

  saveWebData(): void {
    const storage: any = {
      w0: this.language.code ?? 'de_DE',
      w1: this._syncType,
      w2: this.oauth2AccessToken,
      w3: this.theme,
      w4: GLOBALS.ownTheme
    };
    localStorage.setItem('webData', JSON.stringify(storage));
  }

  async requestJson(url: string, params?: { method?: string, options?: any, body?: any, showError?: boolean, asJson?: boolean, timeout?: number }) {
    return this.request(url, params).then(response => {
      return response?.body;
    });
  }

  async request(url: string, params?: { method?: string, options?: any, body?: any, showError?: boolean, asJson?: boolean, timeout?: number }) {
    params ??= {};
    params.method ??= 'get';
    params.showError ??= true;
    params.asJson ??= false;
    params.timeout ??= 1000;
    let response;
    const req = new HttpRequest(params.method, url,
      null,
      params.options);
    try {
      switch (params.method.toLowerCase()) {
        case 'post':
          response = await lastValueFrom(this.http.post(url, params.body, params.options).pipe(timeout({
            each: params.timeout,
            with: () => throwError(() => new CustomTimeoutError())
          })));
          break;
        default:
          response = await lastValueFrom(this.http.request(req).pipe(timeout({
            each: params.timeout,
            with: () => throwError(() => new CustomTimeoutError())
          })));
          break;
      }
    } catch (ex: any) {
      if (ex instanceof CustomTimeoutError) {
        response = $localize`There was no answer within ${params.timeout / 1000} seconds at ${url}`;
      } else if (ex?.messge != null) {
        response = ex.message;
      } else {
        response = ex;
      }
    }
    return params.asJson ? response.body : response;
  }

  saveImmediate(saveToAppData?: () => void, onDone?: () => void) {
    if (this.saveImmediately) {
      saveToAppData?.();
      this.bs.saveAppData(GLOBALS.appData,
        (data) => {
          const ut = GLOBALS.currentUserType?.value;
          GLOBALS.appData.fillFromJson(data.asJson);
          GLOBALS.appData.usertype = data.usertype;
          GLOBALS.currentUserType = GLOBALS.usertypeList.find(e => e.value === ut) ?? GLOBALS.usertypeList[0];
          GLOBALS.saveSharedData();
          onDone?.();
        },
        (error) => {
          console.error(error);
          this.ms.error($localize`Error when saving data - ${error}`);
        });
    }
  }

  private may(key: string): boolean {
    return this.flags.indexOf(`|${key}|`) >= 0;
  }

  private _loadSharedData(storage: any): void {
    this.storageVersion = storage.s1;
  }
}
